import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Text, Card, BlockStack, Box, Grid, FooterHelp, Page, Divider, Link, useBreakpoints, Banner } from '@shopify/polaris';
import { PlusIcon, MinusIcon, EditIcon, ChartVerticalFilledIcon, ChartVerticalIcon, SettingsFilledIcon, LabelPrinterIcon, LayoutSectionIcon, OrderDraftFilledIcon, ClipboardChecklistIcon, InventoryUpdatedIcon } from '@shopify/polaris-icons';

import { findOrCreate } from '../../redux/actions/shops';
import { getSettings } from '../../redux/actions/settings';

import FeatureCard from '../partials/FeatureCard';
import LoadingPlaceholder from '../partials/LoadingPlaceholder';
import RequiresRefreshBanner from '../partials/RequiresRefreshBanner';

const MainPage = ({ shopUrl, payload, shopLoad, shops, settingsLoad, settings, requiresRefresh }) => {  
  useEffect(() => {
    shopLoad(shopUrl, payload);
    settingsLoad(shopUrl, payload);
  }, [shopUrl, payload, shopLoad]);

  const { smUp } = useBreakpoints();

  if (shops.status === 'success' && settings.status === 'success') {
    if(import.meta.env.VITE_PRICING_REDIRECT === '1' && shops.activeSubscriptions.length === 0) {
      return(
        <Page
          divider
          title="ScanWay - Inventory Management"
        >
          <BlockStack gap="500">
            <Banner
              title="Activate ScanWay - Inventory Managment"
              action={{content: 'Start Trial', target: '_top', url: `https://admin.shopify.com/store/${shops.shop.split('.myshopify.com')[0]}/charges/scanway-inventory-management/pricing_plans`}}
              tone="info"
              onDismiss={() => {}}
            >
              <p>Sign up now and start your trial for ScanWay - Inventory Management</p>
            </Banner>

          </BlockStack>
          <FooterHelp>
            By using the ScanWay - Inventory Management app, you acknowledge that you have read and agree to A Thousand Apps Inc.’s <Link url="https://athousandapps.com/terms-of-service/" external>Terms of Service</Link> and <Link url="https://athousandapps.com/privacy-policy/" external>Privacy Notice</Link>, both of which govern your use of this service.
          </FooterHelp>
        </Page>
      )
    }

    return(
      <Page
        divider
        title="ScanWay - Inventory Management"
      >
        { requiresRefresh &&
          <RequiresRefreshBanner />
        }
        <BlockStack gap="500">
          <FeatureCard
            title="Manage and Print Product Barcode Labels"
            description="Search products, manage barcode images and print product labels."
            buttonText="Manage barcodes and print labels"
            image={LabelPrinterIcon}
            linkUrl="/manage"
            stripeBackground='#1774bb'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Label Templates"
            description="Create and update label templates for different printers and label sizes."
            buttonText="Manage label templates"
            linkUrl="/templates"
            showImage={true}
            image={LayoutSectionIcon}
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Create Order"
            description="Create orders by scanning barcodes and setting quantities."
            buttonText="Create Order"
            image={OrderDraftFilledIcon}
            linkUrl="/order"
            showImage={true}
            stripeBackground='#39a0a0'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Order Pick and Verify"
            description="Increase order picking accuracy by scanning items from an order pick list."
            buttonText="Pick and Verify"
            image={ClipboardChecklistIcon}
            linkUrl="/pick"
            showImage={true}
            stripeBackground='#070808'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Live Inventory Modification"
            description="Scan barcodes, check inventory levels and make instant changes to inventory."
            buttonText="Live modify inventory"
            image={InventoryUpdatedIcon}
            linkUrl="/inventory/live"
            stripeBackground='#c66628'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Add to Inventory"
            description="Increase product inventories for products associated with scanned barcodes. Quickly capture information about the products being added to inventory."
            buttonText="Add inventory"
            image={PlusIcon}
            linkUrl="/inventory/add"
            stripeBackground='#2c9c7c'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Update Inventory"
            description="Change product inventories for products associated with scanned barcodes. Update inventory levels for multiple products at the same time."
            buttonText="Update inventory"
            image={EditIcon}
            linkUrl="/inventory/update"
            stripeBackground='#fbe194'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="Reduce Inventory"
            description="Reduce product inventories for products associated with scanned barcodes. Reduce inventory counts for multiple products at the same time."
            buttonText="Reduce inventory"
            image={MinusIcon}
            linkUrl="/inventory/remove"
            stripeBackground='#943737'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="View Batch Changes"
            description="See and download historical changes from the add, update and reduce inventory tools."
            buttonText="View history"
            image={ChartVerticalFilledIcon}
            linkUrl="/update_results"
            stripeBackground='#124b5f'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="View Saved Live Inventory Changes"
            description="Download CSV results from live session updates."
            buttonText="View Live Changes History"
            image={ChartVerticalIcon}
            linkUrl="/live_results"
            stripeBackground='#c66628'
          />

          {smUp ? <Divider /> : null}

          <FeatureCard
            title="App Settings"
            description="View and modify app settings for this store. Generate barcode values and barcode images for all products in the store."
            buttonText="View settings"
            image={SettingsFilledIcon}
            linkUrl="/settings"
            showImage={true}
            stripeBackground='#ef8f2f'
          />

          <FooterHelp>
            By using the ScanWay - Inventory Management app, you acknowledge that you have read and agree to A Thousand Apps Inc.’s <Link url="https://athousandapps.com/terms-of-service/" external>Terms of Service</Link> and <Link url="https://athousandapps.com/privacy-policy/" external>Privacy Notice</Link>, both of which govern your use of this service.
          </FooterHelp>
        </BlockStack>
      </Page>
    )
  }

  return(
      <Page
        divider
        title="ScanWay - Inventory Management"
      >
        { requiresRefresh &&
          <RequiresRefreshBanner />
        }
        <BlockStack gap="500">
          <LoadingPlaceholder />
          {smUp ? <Divider /> : null}
          <LoadingPlaceholder />
          {smUp ? <Divider /> : null}
          <LoadingPlaceholder />
          {smUp ? <Divider /> : null}

          <FooterHelp>
            By using the ScanWay - Inventory Management app, you acknowledge that you have read and agree to A Thousand Apps Inc.’s <Link url="https://athousandapps.com/terms-of-service/" external>Terms of Service</Link> and <Link url="https://athousandapps.com/privacy-policy/" external>Privacy Notice</Link>, both of which govern your use of this service.
          </FooterHelp>
        </BlockStack>
    </Page>
  )
}

const mapStateToProps = state => ({
  requiresRefresh: state.activeToken.requiresRefresh,
  shops: state.shops,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  shopLoad: (storeUrl, payload) => dispatch(findOrCreate(storeUrl, payload)),
  settingsLoad: (storeUrl, payload) => dispatch(getSettings(storeUrl, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainPage);



